import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Audit graph analytics (part 97).
 *
 * The server owns the algorithm: it derives employee, vendor, bank-account and document nodes
 * from master data, weights the relationships, runs Brandes betweenness and stores the snapshot.
 * This page never ranks anything itself — it draws the stored snapshot and prints the stored
 * centrality, so a screenshot and a working paper always agree.
 *
 * The canvas is deliberately layout-free of physics: nodes sit on a circle ordered by kind and
 * betweenness, so the same build draws the same picture on every machine. No graph library.
 */

type Kind = 'employee' | 'vendor' | 'account' | 'document'

interface GraphNode {
  id: string
  key: string
  kind: Kind
  kind_label: string
  label: string
  ref_id?: string
  attributes: Record<string, string>
  degree: number
  weighted_degree: number
  betweenness: number
}

interface GraphEdge {
  from: string
  to: string
  kind: string
  kind_label: string
  weight: number
  evidence: string
}

interface Snapshot {
  id: string
  node_count: number
  edge_count: number
  kind_counts: Record<string, number>
  kind_labels: Record<string, string>
  density: number
  component_count: number
  cycle_count: number
  top_node_key: string
  top_node_label: string
  top_betweenness: number
  computed_at: string
  nodes: GraphNode[]
  edges: GraphEdge[]
}

interface CentralityRow extends GraphNode {
  rank: number
}

interface Neighbour {
  id: string
  key: string
  kind: Kind
  kind_label: string
  label: string
  degree: number
  betweenness: number
  edge_kind: string
  edge_label: string
  weight: number
  evidence: string
}

interface NodeDetail extends GraphNode {
  neighbours: Neighbour[]
}

interface PathNode {
  id: string
  key: string
  kind: Kind
  kind_label: string
  label: string
  betweenness: number
}

interface PathEdge {
  from: string
  to: string
  kind: string
  kind_label: string
  weight: number
  evidence: string
}

interface PathResult {
  found: boolean
  from: PathNode
  to: PathNode
  hops: number
  min_weight: number
  nodes: PathNode[]
  edges: PathEdge[]
  message: string
}

const KIND_ORDER: Record<string, number> = { employee: 0, vendor: 1, account: 2, document: 3 }

const kindColor: Record<string, string> = {
  employee: 'var(--color-gold)',
  vendor: 'var(--color-crimson)',
  account: 'var(--color-emerald)',
  document: 'var(--color-ivory-dim)',
}

const statusLabels: Record<string, string> = {
  active: 'ใช้งานอยู่',
  terminated: 'พ้นสภาพ',
  watchlist: 'เฝ้าระวัง',
  blocked: 'ระงับ',
}

const attributeLabels: Record<string, string> = {
  code: 'รหัส',
  status: 'สถานะ',
  risk_score: 'คะแนนความเสี่ยง',
  bank_suffix: 'เลขท้ายบัญชี',
  holder_count: 'จำนวนผู้ถือบัญชี',
  document_kind: 'ประเภทเอกสาร',
}

function attributeText(key: string, value: string): string {
  if (key === 'status') return statusLabels[value] ?? value
  return value
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function betweennessPct(value: number): string {
  return `${(value * 100).toFixed(2)}%`
}

function truncated(value: string, limit: number): string {
  return value.length > limit ? `${value.slice(0, limit)}…` : value
}

// Layout is a pure function of the snapshot: same nodes, same circle, same picture.
function circleLayout(nodes: GraphNode[], width: number, height: number) {
  const ordered = [...nodes].sort((a, b) => {
    const kind = (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9)
    if (kind !== 0) return kind
    if (b.betweenness !== a.betweenness) return b.betweenness - a.betweenness
    if (b.degree !== a.degree) return b.degree - a.degree
    return a.label.localeCompare(b.label, 'th')
  })
  const visible = ordered.slice(0, 80)
  const positions = new Map<string, { x: number; y: number }>()
  const radius = Math.min(width, height) / 2 - 56
  visible.forEach((node, index) => {
    const angle = (2 * Math.PI * index) / Math.max(1, visible.length) - Math.PI / 2
    positions.set(node.key, {
      x: width / 2 + Math.cos(angle) * radius,
      y: height / 2 + Math.sin(angle) * radius,
    })
  })
  return { visible, positions }
}

function GraphCanvas({
  snapshot,
  selectedId,
  onSelect,
}: {
  snapshot: Snapshot
  selectedId: string
  onSelect: (id: string) => void
}) {
  const width = 760
  const height = 460
  const { visible, positions } = useMemo(
    () => circleLayout(snapshot.nodes, width, height),
    [snapshot.nodes],
  )
  const visibleKeys = useMemo(() => new Set(visible.map((node) => node.key)), [visible])
  const selected = visible.find((node) => node.id === selectedId)
  const labelKeys = useMemo(() => {
    const top = [...visible].sort((a, b) => b.betweenness - a.betweenness).slice(0, 10)
    const keys = new Set(top.map((node) => node.key))
    if (selected) keys.add(selected.key)
    return keys
  }, [visible, selected])

  const edges = snapshot.edges.filter((edge) => visibleKeys.has(edge.from) && visibleKeys.has(edge.to))

  return (
    <div className="card card-pad">
      <div className="row" style={{ alignItems: 'baseline' }}>
        <span className="card-label">แผนที่ความสัมพันธ์</span>
        <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
          {number(visible.length)} จาก {number(snapshot.node_count)} โหนด
        </span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="แผนที่กราฟความสัมพันธ์ระหว่างพนักงาน ผู้ขาย บัญชี และเอกสาร"
        style={{ width: '100%', height: 'auto', marginTop: 8 }}
      >
        <circle cx={width / 2} cy={height / 2} r={Math.min(width, height) / 2 - 56}
          fill="none" stroke="var(--color-hairline)" strokeDasharray="2 8" />
        {edges.map((edge) => {
          const from = positions.get(edge.from)
          const to = positions.get(edge.to)
          if (!from || !to) return null
          const highlight = selected
            ? edge.from === selected.key || edge.to === selected.key
            : false
          return (
            <line
              key={`${edge.from}-${edge.to}-${edge.kind}`}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke={highlight ? 'var(--color-gold)' : 'rgba(200, 169, 106, 0.20)'}
              strokeWidth={highlight ? 1.6 : 0.8 + edge.weight / 90}
              strokeLinecap="round"
            />
          )
        })}
        {visible.map((node) => {
          const position = positions.get(node.key)
          if (!position) return null
          const active = node.id === selectedId
          const radius = 5 + Math.min(7, node.degree * 1.2)
          return (
            <g
              key={node.id}
              transform={`translate(${position.x} ${position.y})`}
              onClick={() => onSelect(node.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onSelect(node.id)
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`${node.kind_label} ${node.label}`}
              style={{ cursor: 'pointer', outline: 'none' }}
            >
              <title>{`${node.label} · ${node.kind_label} · ระดับ ${node.degree} · ความเป็นจุดเชื่อม ${betweennessPct(node.betweenness)}`}</title>
              {active ? (
                <circle r={radius + 5} fill="none" stroke="var(--color-gold)" strokeWidth={1.2} opacity={0.8} />
              ) : null}
              <circle
                r={radius}
                fill={kindColor[node.kind] ?? 'var(--color-ivory-dim)'}
                fillOpacity={active ? 1 : 0.82}
                stroke="var(--color-ink)"
                strokeWidth={1.5}
              />
              {node.betweenness >= 0.2 ? (
                <circle r={2} fill="var(--color-ink)" />
              ) : null}
              {labelKeys.has(node.key) ? (
                <text
                  y={radius + 14}
                  textAnchor="middle"
                  fontSize={10}
                  fill={active ? 'var(--color-ivory)' : 'var(--color-ivory-dim)'}
                >
                  {truncated(node.label, 24)}
                </text>
              ) : null}
            </g>
          )
        })}
      </svg>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        {(Object.keys(snapshot.kind_labels) as Kind[]).map((kind) => (
          <span key={kind} className="chip">
            <i
              className="sev-dot"
              style={{ background: kindColor[kind], marginRight: 6 }}
            />
            {snapshot.kind_labels[kind]} {number(snapshot.kind_counts[kind] ?? 0)}
          </span>
        ))}
        <span className="dim" style={{ fontSize: 10.5, marginLeft: 'auto' }}>
          เส้นหนาขึ้นตามน้ำหนักหลักฐาน · วงแหวนทองคือโหนดที่เลือก
        </span>
      </div>
      <div className="dim" style={{ fontSize: 10.5, marginTop: 8 }}>
        โหนดเอกสารจะปรากฏเมื่อหลักฐานการเบิกตรงกับไฟล์ที่อัปโหลด หรือเมื่อผู้ขายออกเอกสารตามเลขประจำตัวผู้เสียภาษี
      </div>
    </div>
  )
}

function NodePanel({ detail, onSelect }: { detail: NodeDetail; onSelect: (id: string) => void }) {
  const attributes = Object.entries(detail.attributes).filter(([key]) => attributeLabels[key])
  return (
    <div className="card card-pad">
      <div className="card-label">โหนดที่เลือก</div>
      <div style={{ fontSize: 20, marginTop: 8, lineHeight: 1.35 }}>{detail.label}</div>
      <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <span className="chip gold">{detail.kind_label}</span>
        <span className="dim" style={{ fontSize: 11 }}>
          {number(detail.degree)} เส้นเชื่อม · น้ำหนักรวม {number(detail.weighted_degree)}
        </span>
      </div>

      <div className="grid-2" style={{ marginTop: 16 }}>
        <div>
          <div className="dim" style={{ fontSize: 10.5 }}>ระดับ (degree)</div>
          <div className="tnum" style={{ fontSize: 24, fontWeight: 300 }}>{number(detail.degree)}</div>
        </div>
        <div>
          <div className="dim" style={{ fontSize: 10.5 }}>ความเป็นจุดเชื่อม</div>
          <div className="tnum" style={{ fontSize: 24, fontWeight: 300 }}>
            {betweennessPct(detail.betweenness)}
          </div>
        </div>
      </div>
      <div className="bar" style={{ marginTop: 6, width: '100%' }}>
        <i style={{ width: `${Math.min(100, detail.betweenness * 100)}%` }} />
      </div>
      <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>
        สัดส่วนเส้นทางสั้นที่สุดระหว่างคู่อื่นที่ผ่านโหนดนี้ (Brandes, ทำให้อยู่ในช่วง 0-100%)
      </div>

      {attributes.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div className="card-label">ข้อมูลประกอบ</div>
          <div className="stack" style={{ gap: 6, marginTop: 8 }}>
            {attributes.map(([key, value]) => (
              <div key={key} className="row" style={{ alignItems: 'baseline' }}>
                <span className="dim" style={{ fontSize: 11.5 }}>{attributeLabels[key]}</span>
                <span className="tnum" style={{ marginLeft: 'auto', fontSize: 12 }}>
                  {attributeText(key, value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 18 }}>
        <div className="card-label">โหนดข้างเคียง ({number(detail.neighbours.length)})</div>
        <div className="stack" style={{ gap: 0, marginTop: 6 }}>
          {detail.neighbours.length === 0 ? (
            <div className="dim" style={{ fontSize: 12 }}>โหนดนี้ไม่มีความเชื่อมโยงกับโหนดอื่น</div>
          ) : (
            detail.neighbours.map((neighbour) => (
              <button
                key={`${neighbour.key}-${neighbour.edge_kind}`}
                onClick={() => onSelect(neighbour.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 0,
                  borderTop: '1px solid var(--color-hairline)',
                  padding: '9px 0',
                  cursor: 'pointer',
                  color: 'inherit',
                  font: 'inherit',
                }}
              >
                <div className="row" style={{ alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12.5 }}>{truncated(neighbour.label, 40)}</span>
                  <span className="tnum dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
                    น้ำหนัก {neighbour.weight}
                  </span>
                </div>
                <div className="dim" style={{ fontSize: 10.5, marginTop: 2 }}>
                  {neighbour.kind_label} · {neighbour.edge_label}
                  {neighbour.evidence ? ` · ${neighbour.evidence}` : ''}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

function PathFinder({ nodes }: { nodes: GraphNode[] }) {
  const [fromId, setFromId] = useState('')
  const [toId, setToId] = useState('')

  const options = useMemo(
    () => [...nodes].sort((a, b) => a.label.localeCompare(b.label, 'th')),
    [nodes],
  )

  const findPath = useMutation({
    mutationFn: () =>
      api<PathResult>(`/v1/graph/path?from=${encodeURIComponent(fromId)}&to=${encodeURIComponent(toId)}`),
  })

  const result = findPath.data

  return (
    <div className="card card-pad">
      <div className="card-label">หาเส้นทางสั้นที่สุด</div>
      <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
        เลือกสองโหนด ระบบจะเลือกเส้นทางที่ใช้จำนวนเส้นน้อยที่สุด และถ้ามีหลายเส้นทางจะเลือกเส้นที่จุดอ่อนแอที่สุดแข็งแรงกว่า
      </div>
      <div className="grid-2" style={{ marginTop: 14 }}>
        <div className="field" style={{ marginTop: 0 }}>
          <label htmlFor="graph-path-from">จากโหนด</label>
          <select
            id="graph-path-from"
            className="select"
            value={fromId}
            onChange={(event) => {
              setFromId(event.target.value)
              findPath.reset()
            }}
            style={{ width: '100%' }}
          >
            <option value="">เลือกโหนดต้นทาง</option>
            {options.map((node) => (
              <option key={node.id} value={node.id}>{node.kind_label} · {node.label}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ marginTop: 0 }}>
          <label htmlFor="graph-path-to">ถึงโหนด</label>
          <select
            id="graph-path-to"
            className="select"
            value={toId}
            onChange={(event) => {
              setToId(event.target.value)
              findPath.reset()
            }}
            style={{ width: '100%' }}
          >
            <option value="">เลือกโหนดปลายทาง</option>
            {options.map((node) => (
              <option key={node.id} value={node.id}>{node.kind_label} · {node.label}</option>
            ))}
          </select>
        </div>
      </div>
      <button
        className="btn btn-primary"
        style={{ marginTop: 14 }}
        disabled={!fromId || !toId || findPath.isPending}
        onClick={() => findPath.mutate()}
      >
        {findPath.isPending ? 'กำลังหาเส้นทาง…' : 'หาเส้นทาง'}
      </button>

      {findPath.isError ? (
        <div className="form-error">{errorText(findPath.error, 'หาเส้นทางไม่สำเร็จ')}</div>
      ) : null}

      {result ? (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--color-hairline)', paddingTop: 14 }}>
          <div className="row" style={{ alignItems: 'baseline' }}>
            <span className="card-label">ผลลัพธ์</span>
            <span className="tnum dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
              {result.found ? `${result.hops} เส้น · จุดอ่อนแอสุด ${result.min_weight}` : 'ไม่พบเส้นทาง'}
            </span>
          </div>
          <div style={{ fontSize: 12.5, marginTop: 8 }}>{result.message}</div>
          {result.found ? (
            <>
              <div className="stack" style={{ gap: 8, marginTop: 10 }}>
                {result.nodes.map((node, index) => (
                  <div key={`${node.id}-${index}`} className="row" style={{ alignItems: 'baseline' }}>
                    <span className="tnum dim" style={{ fontSize: 10.5, width: 18 }}>{index + 1}</span>
                    <div>
                      <div style={{ fontSize: 12.5 }}>{node.label}</div>
                      <div className="dim" style={{ fontSize: 10.5 }}>{node.kind_label}</div>
                    </div>
                    {index < result.edges.length ? (
                      <span className="dim" style={{ marginLeft: 'auto', fontSize: 10.5, textAlign: 'right' }}>
                        {result.edges[index].kind_label}
                        {' · '}
                        น้ำหนัก {result.edges[index].weight}
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
              <div className="dim" style={{ fontSize: 10.5, marginTop: 10 }}>
                เกณฑ์: จำนวนเส้นน้อยที่สุดก่อน แล้วจึงเลือกเส้นทางที่น้ำหนักเส้นอ่อนแอที่สุดสูงกว่า
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function Graph() {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const snapshot = useQuery({
    queryKey: ['graph-snapshot'],
    queryFn: () => api<Snapshot>('/v1/graph/snapshot'),
    staleTime: 30_000,
    retry: false,
  })

  const centrality = useQuery({
    queryKey: ['graph-centrality'],
    queryFn: () => api<CentralityRow[]>('/v1/graph/centrality?limit=12'),
    staleTime: 30_000,
    retry: false,
  })

  const notBuilt =
    snapshot.error instanceof ApiError && (snapshot.error.status === 404 || snapshot.error.code === 'GRAPH_NOT_BUILT')

  const activeId =
    selectedId ||
    snapshot.data?.nodes.find((node) => node.key === snapshot.data?.top_node_key)?.id ||
    ''

  const detail = useQuery({
    queryKey: ['graph-node', activeId],
    queryFn: () => api<NodeDetail>(`/v1/graph/nodes/${activeId}`),
    enabled: Boolean(activeId),
    staleTime: 60_000,
  })

  const build = useMutation({
    mutationFn: () => apiWrite<{ node_count: number; edge_count: number; computed_at: string }>(
      '/v1/graph/snapshots',
      'POST',
    ),
    onSuccess: (result) => {
      setSelectedId('')
      setNotice(`สร้างกราฟแล้ว · ${number(result.node_count)} โหนด · ${number(result.edge_count)} เส้นเชื่อม`)
      void qc.invalidateQueries({ queryKey: ['graph-snapshot'] })
      void qc.invalidateQueries({ queryKey: ['graph-centrality'] })
      void qc.invalidateQueries({ queryKey: ['graph-node'] })
    },
  })

  const maxBetweenness = Math.max(
    0.0001,
    ...(centrality.data ?? []).map((row) => row.betweenness),
  )

  return (
    <>
      <PageHead
        title="กราฟความสัมพันธ์"
        sub="เชื่อมโยงพนักงาน ผู้ขาย บัญชีรับเงิน และเอกสารหลักฐาน เพื่อหาจุดเชื่อมที่ควบคุมเส้นทางระหว่างกลุ่ม"
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => build.mutate()} disabled={build.isPending}>
            {build.isPending ? 'กำลังสร้างกราฟ…' : 'สร้างกราฟใหม่'}
          </button>
        }
      />

      {notice ? (
        <div className="chip gold" style={{ marginBottom: 16 }} role="status">{notice}</div>
      ) : null}

      {build.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(build.error, 'สร้างกราฟไม่สำเร็จ')}
        </div>
      ) : null}

      {snapshot.isLoading ? (
        <div className="stack"><Skel h={180} /><Skel h={280} /></div>
      ) : notBuilt ? (
        <div className="card card-pad">
          <Empty
            title="ยังไม่มีกราฟความสัมพันธ์"
            body="กด “สร้างกราฟใหม่” เพื่ออ่านข้อมูลพนักงาน ผู้ขาย บัญชีธนาคาร และเอกสาร แล้วคำนวณระดับความเป็นจุดเชื่อมและเส้นทางสั้นที่สุด"
          />
        </div>
      ) : snapshot.isError ? (
        <div className="card card-pad">
          <div className="form-error">{errorText(snapshot.error, 'โหลดกราฟไม่สำเร็จ')}</div>
        </div>
      ) : snapshot.data ? (
        <>
          <div className="grid-4 stagger">
            <Stat
              label="โหนดในกราฟ"
              value={number(snapshot.data.node_count)}
              unit="โหนด"
              foot={`${number(snapshot.data.kind_counts.employee ?? 0)} พนักงาน · ${number(snapshot.data.kind_counts.vendor ?? 0)} ผู้ขาย`}
            />
            <Stat
              label="เส้นเชื่อม"
              value={number(snapshot.data.edge_count)}
              unit="เส้น"
              foot={`บัญชี ${number(snapshot.data.kind_counts.account ?? 0)} · เอกสาร ${number(snapshot.data.kind_counts.document ?? 0)}`}
            />
            <Stat
              label="จุดเชื่อมสูงสุด"
              value={betweennessPct(snapshot.data.top_betweenness)}
              unit="betweenness"
              foot={snapshot.data.top_node_label || 'ไม่ระบุ'}
            />
            <Stat
              label="องค์ประกอบเชื่อมโยง"
              value={number(snapshot.data.component_count)}
              unit="กลุ่ม"
              foot={`ความหนาแน่น ${(snapshot.data.density * 100).toFixed(1)}% · วงจรสั้น ${number(snapshot.data.cycle_count)}`}
            />
          </div>

          <div className="grid-main" style={{ marginTop: 16 }}>
            <GraphCanvas
              snapshot={snapshot.data}
              selectedId={activeId}
              onSelect={(id) => setSelectedId(id)}
            />
            {detail.isLoading ? (
              <div className="card card-pad"><Skel h={300} /></div>
            ) : detail.isError ? (
              <div className="card card-pad">
                <div className="form-error">{errorText(detail.error, 'โหลดรายละเอียดโหนดไม่สำเร็จ')}</div>
              </div>
            ) : detail.data ? (
              <NodePanel detail={detail.data} onSelect={(id) => setSelectedId(id)} />
            ) : (
              <div className="card card-pad">
                <Empty title="ยังไม่ได้เลือกโหนด" body="เลือกวงกลมบนแผนที่หรือแถวในตารางเพื่อดูโหนดข้างเคียงและหลักฐานของเส้นเชื่อม" />
              </div>
            )}
          </div>

          <div className="grid-main" style={{ marginTop: 16 }}>
            <div className="card card-pad">
              <div className="row" style={{ alignItems: 'baseline' }}>
                <span className="card-label">อันดับความเป็นจุดเชื่อม</span>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
                  Brandes · จากมากไปน้อย
                </span>
              </div>
              <div className="table-wrap" style={{ marginTop: 10 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th style={{ width: 44 }}>#</th>
                      <th>โหนด</th>
                      <th style={{ textAlign: 'right' }}>ระดับ</th>
                      <th style={{ textAlign: 'right' }}>น้ำหนักรวม</th>
                      <th>betweenness</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(centrality.data ?? []).map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedId(row.id)}
                        style={row.id === activeId ? { background: 'rgba(200, 169, 106, 0.06)' } : undefined}
                      >
                        <td className="tnum dim">{row.rank}</td>
                        <td>
                          <div>{truncated(row.label, 52)}</div>
                          <div className="dim" style={{ fontSize: 10.5 }}>
                            {row.kind_label}
                            {row.attributes.code ? ` · ${row.attributes.code}` : ''}
                          </div>
                        </td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{number(row.degree)}</td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{number(row.weighted_degree)}</td>
                        <td>
                          <div className="row" style={{ gap: 8 }}>
                            <div className="bar" style={{ flex: 1 }}>
                              <i style={{ width: `${Math.max(2, (row.betweenness / maxBetweenness) * 100)}%` }} />
                            </div>
                            <span className="tnum dim" style={{ fontSize: 10.5, width: 52, textAlign: 'right' }}>
                              {betweennessPct(row.betweenness)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="dim" style={{ fontSize: 10.5, marginTop: 10 }}>
                ค่าความเป็นจุดเชื่อมคำนวณจากโครงสร้างเส้นเชื่อม ไม่ถ่วงน้ำหนัก เพื่อให้เห็นโหนดที่เป็นทางผ่านเดียวระหว่างกลุ่ม
                ส่วนน้ำหนักเส้นเชื่อมใช้ตัดสินเมื่อมีเส้นทางสั้นที่สุดหลายเส้นทาง
              </div>
            </div>

            <PathFinder nodes={snapshot.data.nodes} />
          </div>

          <div className="dim" style={{ fontSize: 10.5, marginTop: 14 }}>
            สร้างเมื่อ {thaiDateTime(snapshot.data.computed_at)} · รหัสกราฟ {snapshot.data.id.slice(0, 8)} ·
            เก็บไว้ {number(5)} รุ่นล่าสุดต่อองค์กร
          </div>
        </>
      ) : null}
    </>
  )
}
